import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, TableShell, Loading, EmptyState } from "@/components/common";
import UploadDialog from "@/components/modals/UploadDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { gbp, num, formatDate } from "@/lib/format";
import { Plus, UploadCloud, Search, Pencil, Trash2, Download } from "lucide-react";

const BLANK = { product_code: "", description: "", product_cat: "", spend_2024: 0, spend_2025: 0, spend_2026: 0 };

export default function ProductsTable() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });

  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const cats = useMemo(() => [...new Set(data.map((d) => d.product_cat).filter(Boolean))].sort(), [data]);
  const filtered = useMemo(() => data.filter((r) => {
    if (cat !== "all" && r.product_cat !== cat) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!`${r.product_code} ${r.description} ${r.product_cat}`.toLowerCase().includes(s)) return false;
    }
    return true;
  }), [data, cat, search]);

  const openAdd = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ product_code: r.product_code, description: r.description, product_cat: r.product_cat, spend_2024: r.spend_2024, spend_2025: r.spend_2025, spend_2026: r.spend_2026 });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.product_code.trim()) { toast.error("Product code is required"); return; }
    setSaving(true);
    try {
      const p = { ...form };
      ["spend_2024", "spend_2025", "spend_2026"].forEach((k) => (p[k] = Number(p[k]) || 0));
      if (editing) await api.put(`/products/${editing.id}`, p);
      else await api.post("/products", p);
      toast.success(editing ? "Product updated" : "Product added");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setDialogOpen(false);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/products/${deleteTarget.id}`);
      toast.success("Product deleted");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setDeleteTarget(null); }
  };

  const exportCsv = () => {
    const header = ["product_code", "description", "product_cat", "spend_2024", "spend_2025", "spend_2026"];
    const rows = filtered.map((r) => header.map((h) => `"${r[h] ?? ""}"`).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "asda-ipl-suppliers.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <PageHeader title="Suppliers" subtitle="IPL packaging suppliers grouped by category, with 2024–2026 spend. Upload to keep values up to date.">
        <Button variant="outline" onClick={exportCsv} data-testid="product-export-button"><Download className="w-4 h-4 mr-1.5" /> Export</Button>
        <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="product-upload-button"><UploadCloud className="w-4 h-4 mr-1.5" /> Upload</Button>
        <Button onClick={openAdd} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="product-add-button"><Plus className="w-4 h-4 mr-1.5" /> Add supplier</Button>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search supplier, code or category…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="product-search-input" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full sm:w-52" data-testid="product-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (
        <TableShell>
          <table className="w-full text-sm" data-testid="products-table">
            <thead>
              <tr className="bg-slate-100 dark:bg-slate-800/80 text-left text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-400">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Packaging Category</th>
                <th className="px-4 py-3 text-right">2024 Spend</th>
                <th className="px-4 py-3 text-right">2025 Spend</th>
                <th className="px-4 py-3 text-right">2026 Spend</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`product-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono-num font-medium text-slate-800 dark:text-slate-200">{r.product_code}</td>
                  <td className="px-4 py-3 text-slate-700 dark:text-slate-300 font-medium">{r.description || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{r.product_cat || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.spend_2024 ? gbp(r.spend_2024) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.spend_2025 ? gbp(r.spend_2025) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{r.spend_2026 ? gbp(r.spend_2026) : "—"}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold text-slate-800 dark:text-slate-200">{gbp((r.spend_2024 || 0) + (r.spend_2025 || 0) + (r.spend_2026 || 0))}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(r)} data-testid={`product-edit-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 hover:text-red-600" onClick={() => setDeleteTarget(r)} data-testid={`product-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <EmptyState message="No products match your filters." />}
        </TableShell>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="product-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label>Code *</Label><Input className="mt-1.5" value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} data-testid="product-form-code" /></div>
            <div><Label>Packaging category</Label><Input className="mt-1.5" value={form.product_cat} onChange={(e) => setForm({ ...form, product_cat: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Supplier name</Label><Input className="mt-1.5" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div><Label>2024 spend (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.spend_2024} onChange={(e) => setForm({ ...form, spend_2024: e.target.value })} /></div>
            <div><Label>2025 spend (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.spend_2025} onChange={(e) => setForm({ ...form, spend_2025: e.target.value })} /></div>
            <div><Label>2026 spend (£)</Label><Input className="mt-1.5" type="number" step="0.01" value={form.spend_2026} onChange={(e) => setForm({ ...form, spend_2026: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#78BE20] hover:bg-[#5fa016]" data-testid="product-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        entity="products"
        columns={["product_code", "description", "product_cat", "spend_2024", "spend_2025", "spend_2026"]}
        onDone={() => { qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete supplier?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove <span className="font-medium">{deleteTarget?.product_code}</span>.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
